import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { MediaType } from '@pixacare/domain';
import { FlattenedSequence } from '@pixacare/dtos';
import { MediaLocalPreviewService } from '@services/media-local-preview/media-local-preview.service';
import { mediaContentKey } from '@shared/store/sequences/media-content-key';
import { sequencesActions } from '@shared/store/sequences/sequences.actions';
import { toOptimisticMedia } from '@helpers/to-optimistic-media';
import { isVideoFile } from './is-video-file';
import { SequenceVideoUploadService } from './sequence-video-upload.service';

@Injectable({ providedIn: 'root' })
export class SequenceMediaUploadService {
  private readonly store = inject(Store);
  private readonly videoUploadService = inject(SequenceVideoUploadService);
  private readonly mediaLocalPreviewService = inject(MediaLocalPreviewService);

  // Single entry point for both the create and edit dialogs. Placeholders show up front (an image
  // previews itself, a video gets a gray square) and are replaced by content key once refetched.
  attachMedias(clientCode: string, sequence: FlattenedSequence, files: File[]): void {
    if (files.length === 0) {
      return;
    }

    const sequenceId = sequence.sequenceInstance.id;

    this.store.dispatch(
      sequencesActions.insertOptimisticMedias({
        clientCode,
        sequenceId,
        medias: files.map((file) =>
          isVideoFile(file)
            ? toOptimisticMedia(clientCode, sequenceId, file.name, file.size, MediaType.VIDEO)
            : toOptimisticMedia(
                clientCode,
                sequenceId,
                file.name,
                file.size,
                MediaType.IMAGE,
                this.mediaLocalPreviewService.register(
                  mediaContentKey({ originalFilename: file.name, size: file.size }),
                  file,
                ),
              ),
        ),
      }),
    );

    const videos = files.filter(isVideoFile);
    const images = files.filter((file) => !isVideoFile(file));

    // No-op when there are no videos.
    this.videoUploadService.uploadVideos(clientCode, sequence, videos);

    if (images.length > 0) {
      this.store.dispatch(sequencesActions.uploadSequenceMedias({ clientCode, sequenceId, medias: images }));
    }
  }
}
